/**
 * BracketView — Visual bracket display for tournaments.
 * Shows rounds as columns with match cards connected by lines.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { getBracketView, type TournamentMatch } from '@/lib/tournaments';
import { Trophy, Swords } from 'lucide-react-native';

interface BracketViewProps {
  tournamentId: string;
  /** Profile lookup for displaying names */
  profileNames?: Map<string, string>;
}

function MatchCard({
  match,
  profileNames,
  colors,
}: {
  match: TournamentMatch;
  profileNames?: Map<string, string>;
  colors: any;
}) {
  const getName = (id: string | null) => {
    if (!id) return 'TBD';
    return profileNames?.get(id) || id.slice(0, 8);
  };

  const isComplete = match.status === 'completed';
  const isBye = match.status === 'bye';

  return (
    <View style={[styles.matchCard, { backgroundColor: colors.surface, borderColor: isComplete ? colors.accent + '40' : colors.border }]}>
      <View style={[styles.playerRow, match.winner_id === match.player_a_id && isComplete && { backgroundColor: colors.accent + '15' }]}>
        <Text style={[styles.seed, { color: colors.textTertiary }]}>A</Text>
        <Text style={[styles.playerName, { color: match.winner_id === match.player_a_id ? colors.accent : colors.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
          {getName(match.player_a_id)}
        </Text>
        {match.winner_id === match.player_a_id && <Trophy size={12} color={colors.accent} />}
      </View>
      <View style={[styles.divider, { backgroundColor: colors.border }]} />
      <View style={[styles.playerRow, match.winner_id === match.player_b_id && isComplete && { backgroundColor: colors.accent + '15' }]}>
        <Text style={[styles.seed, { color: colors.textTertiary }]}>B</Text>
        <Text style={[styles.playerName, { color: match.winner_id === match.player_b_id ? colors.accent : colors.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
          {getName(match.player_b_id)}
        </Text>
        {match.winner_id === match.player_b_id && <Trophy size={12} color={colors.accent} />}
      </View>
      {isBye && (
        <View style={[styles.byeBadge, { backgroundColor: colors.surfaceVariant }]}>
          <Text style={[styles.byeText, { color: colors.textTertiary }]}>BYE</Text>
        </View>
      )}
    </View>
  );
}

export default function BracketView({ tournamentId, profileNames }: BracketViewProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [bracket, setBracket] = useState<Map<number, TournamentMatch[]>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBracketView(tournamentId).then(b => {
      setBracket(b);
      setLoading(false);
    });
  }, [tournamentId]);

  if (loading) return <ActivityIndicator color={c.accent} style={{ padding: 40 }} />;

  const rounds = Array.from(bracket.keys()).sort((a, b) => a - b);
  const roundLabels: Record<number, string> = {};
  const totalRounds = rounds.length;
  if (totalRounds >= 1) roundLabels[rounds[totalRounds - 1]] = 'FINAL';
  if (totalRounds >= 2) roundLabels[rounds[totalRounds - 2]] = 'SEMI-FINAL';
  if (totalRounds >= 3) roundLabels[rounds[totalRounds - 3]] = 'QUARTER-FINAL';

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.container}>
      {rounds.map((round) => {
        const matches = bracket.get(round) || [];
        const label = roundLabels[round] || `ROUND ${round}`;
        return (
          <View key={round} style={styles.round}>
            <Text style={[styles.roundLabel, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
              {label}
            </Text>
            <View style={[styles.matchesColumn, { minHeight: matches.length * 90 }]}>
              {matches.map((match) => (
                <MatchCard key={match.id} match={match} profileNames={profileNames} colors={c} />
              ))}
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: SPACE.md, gap: SPACE.md, paddingBottom: 20 },
  round: { width: 180, gap: SPACE.sm },
  roundLabel: { fontSize: 11, letterSpacing: 1.5, textAlign: 'center', marginBottom: 4 },
  matchesColumn: { gap: SPACE.md, justifyContent: 'space-around' },
  matchCard: { borderWidth: 1, borderRadius: RADIUS.xs },
  playerRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 10, gap: 6 },
  seed: { fontSize: 11, width: 16 },
  playerName: { flex: 1, fontSize: 12 },
  divider: { height: 1 },
  byeBadge: { position: 'absolute', right: 6, top: '50%', marginTop: -9, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 2 },
  byeText: { fontSize: 11, letterSpacing: 0.5 },
});
